// Phase 43 — <ProvenancePopover /> (Plan 43-04 Task 2).
// Popover content body for <ProvenanceChip>. Renders source heading +
// confidence bar + optional reasoning + action buttons. Typography locked
// to UI-SPEC: 16px font-semibold heading, 14px font-normal body text.
// Raw-HTML injection is never used here — React default text-node escaping
// keeps reasoning (LLM-generated text) safe from XSS (T-43-15).

import {
  Sparkles,
  Camera,
  FileText,
  Fingerprint,
  Globe,
} from 'lucide-react';
import { Button } from '@marine/ui/button';
import { Progress } from '@marine/ui/progress';
import type { ProvenanceEntry } from '@/modules/inventory/types/boat.types';

interface ProvenancePopoverProps {
  entry: ProvenanceEntry;
  onClear?: () => void;
  onChangeSource?: () => void;
  onRestoreAiValue?: () => void;
}

// Per UI-SPEC §"Copywriting Contract" — popover heading per source. Phase 45.5
// (D-01): 'URL' retired; the 4 doc-typed packet sources name the exact document.
const HEADING: Record<ProvenanceEntry['source'], string> = {
  AI: 'AI-filled',
  'AI-photo': 'From photo analysis',
  PDF: 'From factory packet',
  VIN: 'From VIN lookup',
  manual: 'Manual entry',
  'COO-boat': 'From boat Certificate of Origin',
  'COO-trailer': 'From trailer Certificate of Origin',
  BOL: 'From bill of lading',
  invoice: 'From dealer invoice',
  web: 'From the catalogue',
};

function iconFor(source: ProvenanceEntry['source']) {
  switch (source) {
    case 'AI':
      return Sparkles;
    case 'AI-photo':
      return Camera;
    case 'PDF':
    case 'COO-boat':
    case 'COO-trailer':
    case 'BOL':
    case 'invoice':
      return FileText;
    case 'VIN':
      return Fingerprint;
    case 'web':
      return Globe;
    case 'manual':
    default:
      return Sparkles;
  }
}

export function ProvenancePopover({
  entry,
  onClear,
  onChangeSource,
  onRestoreAiValue,
}: ProvenancePopoverProps) {
  const pct = Math.round(entry.confidence * 100);
  const isAiEdited = entry.userEdited === true;
  const heading = isAiEdited ? 'AI-filled, you edited' : HEADING[entry.source];
  const Icon = iconFor(entry.source);

  return (
    <div className="space-y-2 max-w-[240px]">
      <div className="flex items-center gap-2">
        <Icon
          className="h-4 w-4 text-violet-600 dark:text-violet-400"
          role="img"
        />
        {/* 16px font-semibold per UI-SPEC §Typography */}
        <span className="text-base font-semibold">{heading}</span>
      </div>

      <Progress value={pct} className="h-1.5" />

      {/* 14px font-normal body text per UI-SPEC §Typography. React escapes
          {entry.reasoning} automatically (see T-43-15 threat model). */}
      <p className="text-sm font-normal text-foreground">
        {entry.reasoning ?? 'No reasoning available'}
      </p>

      <div className="flex gap-1 flex-wrap">
        {isAiEdited && onRestoreAiValue && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRestoreAiValue}
            className="font-normal"
          >
            Restore AI value
          </Button>
        )}
        {onClear && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="text-destructive font-normal"
          >
            Clear field
          </Button>
        )}
        {onChangeSource && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onChangeSource}
            className="font-normal"
          >
            Change source
          </Button>
        )}
      </div>
    </div>
  );
}
